import type { ApiResponse } from '../types/content';
import { logger } from '../utils/logger';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

// Cache for API responses
const cache = new Map<string, { data: ApiResponse<unknown>; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Request deduplication - track in-flight requests
const inFlightRequests = new Map<string, Promise<ApiResponse<unknown>>>();

async function fetchApi<T>(endpoint: string): Promise<ApiResponse<T>> {
	const cacheKey = endpoint;
	const now = Date.now();

	// Check cache first
	const cached = cache.get(cacheKey);
	if (cached && now - cached.timestamp < CACHE_DURATION) {
		return cached.data as ApiResponse<T>;
	}

	// Check if request is already in flight
	const inFlight = inFlightRequests.get(cacheKey);
	if (inFlight) {
		return inFlight as Promise<ApiResponse<T>>;
	}

	// Create new request
	const requestPromise = (async () => {
		try {
			const response = await fetch(`${API_BASE_URL}/api${endpoint}`);
			
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			
			const data = await response.json();
			
			// Cache successful responses
			cache.set(cacheKey, { data, timestamp: now });
			
			return data;
		} catch (error) {
			logger.error(`API Error fetching ${endpoint}:`, error);
			throw error;
		} finally {
			// Remove from in-flight requests
			inFlightRequests.delete(cacheKey);
		}
	})();

	// Store in-flight request
	inFlightRequests.set(cacheKey, requestPromise);

	return requestPromise as Promise<ApiResponse<T>>;
}

export const api = {
	getHome: () => fetchApi('/content/home'),
	getServices: () => fetchApi('/content/services'),
	getGallery: () => fetchApi('/content/gallery'),
	getTestimonials: () => fetchApi('/content/testimonials'),
	getContact: () => fetchApi('/content/contact'),
	getFAQ: () => fetchApi('/content/faq'),
	getAreasCovered: () => fetchApi('/content/areas-covered'),
	getSite: () => fetchApi('/content/site'),
	getFooter: () => fetchApi('/content/footer'),
};
